import { useState } from 'react'
import { Check, Minus } from 'lucide-react'
import PlanCards, { LINE_COPY, LineSwitch } from '../components/PlanCards'

// Pricing, shown on the landing page: a switch for the line of business
// (Invoice, Store, Contract), its three plans as cards, a comparison that
// folds open, and questions. Prices come from the server (/auth/plans), so
// they live in one place (modules.PLANS).

// The comparison: each row and the module it needs ('a|b' means either).
const ROWS = [
  ['Quotes and invoices', 'invoices'],
  ['Jobs: deposits, stages, change orders, retainage', 'jobs'],
  ['Point of sale', 'pos'],
  ['Vendor bills for services', 'invoices|purchase'],
  ['Purchase orders and product bills', 'purchase'],
  ['Inventory', 'inventory'],
  ['Bin tracking', 'detailed_inventory'],
  ['Production: make products from materials', 'production'],
  ['Accounting and financial reports', 'accounting'],
  ['Expenses', 'expenses'],
]

const FAQ = [
  ['Which line is mine?', 'Invoice if you bill customers on invoice, Store if you sell at a counter, Contract if your work comes in jobs or custom orders, as for trades, tailors and furniture makers. If you do a bit of another, add it: point of sale, invoices and inventory are add-ons on the plans that don’t include them.'],
  ['How does the free trial work?', 'Pick a plan and any add-ons, create your company and use everything in them for 30 days. No card is needed to start. Before the trial ends we’ll be in touch about carrying on.'],
  ['How am I billed?', 'Once a month, by invoice, for your plan, your add-ons, and any extra users or locations.'],
  ['Who counts as a user?', 'Anyone who signs in, except cashiers. Basic includes 3 users and Advanced 5; more are $8 a month each. Cashiers, who only use the point of sale, are free, however many you have.'],
  ['What is a location?', 'A store, warehouse or site that keeps its own stock and sales tax. Basic includes one and Advanced two; more are $25 a month each.'],
  ['Contract: what are “invoices not from a job”?', 'On Contract every quote and invoice belongs to a job. To bill customers outside a job too, add Invoices to Contract Basic for $10 a month. Contract Advanced includes it.'],
  ['Can I make my own products?', 'Yes, with the Production add-on ($20 a month) on any plan with Inventory. Record each batch, such as 50 T-shirts: add the materials (from stock or bought for the batch), labour and overhead, and on finishing the total is divided by the units made, so each one goes into stock at what it cost to make.'],
  ['Which plans include accounting?', 'Only Advanced, in every line: the statements, ledgers, bank reconciliation, fixed assets and the month-end close. Basic doesn’t show any accounting, but your sales, bills and payments are still recorded, so moving up to Advanced later shows your whole history from day one.'],
  ['Do I need an accountant to use Advanced?', 'No. Every sale, bill, payment and stock count posts to the books by itself; an accountant can review them whenever you like.'],
  ['Can my accountant work in my books?', 'Yes, on any plan. Invite them under Settings → Accountant and choose Full books or View only; they work from their own free ASYS practice, don’t count toward your users, and you can remove them at any time.'],
  ['Can I change plans later?', 'Yes. Move between Basic and Advanced, switch to another line, or add and drop add-ons, and nothing you’ve recorded is lost.'],
]

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
const has = (plan, rule) => rule.split('|').some((m) => plan.modules.includes(m))

function Cell({ plan, rule }) {
  if (has(plan, rule)) return <span className="pr-yes"><Check size={16} aria-label="Included" /></span>
  const addon = plan.addons.find((a) => rule.split('|').includes(a.key))
  if (rule === 'invoices' && plan.modules.includes('jobs')) {
    return <span className="pr-addon">From a job{addon ? `; others +${money(addon.price)}` : ''}</span>
  }
  if (addon) return <span className="pr-addon">Add-on, {money(addon.price)}</span>
  return <span className="pr-no"><Minus size={16} aria-label="Not available" /></span>
}

/** One line's plans side by side, folded away until opened. */
export function CompareDetails({ data, line }) {
  const plans = (data?.plans || []).filter((p) => p.line === line)
  if (!plans.length) return null
  // Rows no plan in this line has or offers would only be dashes.
  const rows = ROWS.filter(([, rule]) => plans.some((p) => has(p, rule) || p.addons.some((a) => rule.split('|').includes(a.key))))
  return (
    <details className="pr-compare">
      <summary>Compare the {data.lines.find((l) => l.id === line)?.label} plans</summary>
      <div className="pr-table-wrap">
        <table className="pr-table">
          <thead>
            <tr><th scope="col">What’s included</th>{plans.map((p) => <th key={p.id} scope="col">{p.label}<span>{money(p.price)}/mo</span></th>)}</tr>
          </thead>
          <tbody>
            {rows.map(([label, rule]) => (
              <tr key={label}><th scope="row">{label}</th>{plans.map((p) => <td key={p.id}><Cell plan={p} rule={rule} /></td>)}</tr>
            ))}
            <tr><th scope="row">Users included</th>{plans.map((p) => <td key={p.id}>{p.users}</td>)}</tr>
            <tr><th scope="row">Locations included</th>{plans.map((p) => <td key={p.id}>{p.locations}</td>)}</tr>
            <tr><th scope="row">Cashiers</th>{plans.map((p) => <td key={p.id}>Free</td>)}</tr>
          </tbody>
        </table>
      </div>
    </details>
  )
}

/** The line switch, its three plans and their comparison. `data` is /auth/plans. */
export function PricingPlans({ data, error }) {
  const [line, setLine] = useState(() => new URLSearchParams(window.location.search).get('line') || 'invoice')
  return (
    <div className="pr-plans-block">
      {error && <p className="pub-error" role="alert">{error}</p>}
      {data && (
        <>
          <div className="pr-switch-row"><LineSwitch lines={data.lines} value={line} onChange={setLine} /></div>
          <p className="pc-line-who">{LINE_COPY[line]}</p>
          <PlanCards data={data} line={line} action={(p) => ({ label: 'Start free trial', to: `/signup?plan=${p.id}`, primary: p.tier === 'advanced' })} />
          <p className="pr-every">
            Every plan: expenses with receipts and free cashiers. Accounting comes with Advanced. Extra users {money(data.extra_user)} and extra locations {money(data.extra_location)} a month each. Prices in {data.currency}.
          </p>
          <CompareDetails data={data} line={line} />
        </>
      )}
    </div>
  )
}

/** Common questions, each folded until opened. */
export function PricingQuestions() {
  return (
    <div className="pr-faq">
      {FAQ.map(([q, a]) => (
        <details key={q}>
          <summary>{q}</summary>
          <p>{a}</p>
        </details>
      ))}
    </div>
  )
}
